/**
 * Eric 2026-05-12 — Modale Historique des révisions du transcript.
 * Extraite de VideoDetailPage.js dans le cadre du refacto P2 phase 2.
 * Affiche la liste des révisions + bouton "Restaurer" pour les admins.
 */
import React, { useState } from "react";
import axios from "axios";
import { X, ClockCounterClockwise, CircleNotch, ArrowClockwise, CaretDown, CaretRight } from "@phosphor-icons/react";
import RevisionDiffPanel from "./RevisionDiffPanel";

export default function TranscriptHistoryModal({
  open,
  onClose,
  revisions = [],
  loading = false,
  isAdmin = false,
  restoringId = null,
  onRestore,
  lang = "fr",
  api = "",
  videoId = "",
}) {
  // Tâche 1352 (Eric 2026-06-30) — Les changements d'une révision sont
  // chargés À LA DEMANDE : le calcul relit deux instantanés de transcription
  // par ligne, les charger tous à l'ouverture téléchargerait des mégaoctets
  // pour une seule ligne réellement lue.
  const [ouvert, setOuvert] = useState({});
  const [diffs, setDiffs] = useState({});
  const [enCours, setEnCours] = useState({});
  const [erreurs, setErreurs] = useState({});

  const basculer = async (revId) => {
    const dejaOuvert = !!ouvert[revId];
    setOuvert((o) => ({ ...o, [revId]: !dejaOuvert }));
    if (dejaOuvert || diffs[revId] || enCours[revId]) return;
    setEnCours((e) => ({ ...e, [revId]: true }));
    setErreurs((e) => ({ ...e, [revId]: null }));
    try {
      const { data } = await axios.get(
        `${api}/api/videos/${videoId}/transcript-revisions/${revId}/diff`,
        { withCredentials: true },
      );
      setDiffs((d) => ({ ...d, [revId]: data }));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      setErreurs((e) => ({
        ...e,
        [revId]: (lang === "fr" ? "Lecture impossible : " : "Read failed: ") + detail,
      }));
    } finally {
      setEnCours((e) => ({ ...e, [revId]: false }));
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="transcript-history-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between bg-zinc-50">
          <div>
            <h3 className="font-bold text-base text-zinc-900 flex items-center gap-2">
              <ClockCounterClockwise size={18} weight="bold" className="text-amber-600" />
              {lang === "fr" ? "Historique des modifications" : "Modification history"}
            </h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              {lang === "fr"
                ? "Chaque modification (regroupement, re-fusion, correction) est sauvegardée. Restaurez n'importe quelle version."
                : "Every modification is saved. Restore any version."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-zinc-200 rounded"
            data-testid="close-history-btn"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-zinc-500 text-sm">
              <CircleNotch size={24} className="mx-auto mb-2 animate-spin" />
              {lang === "fr" ? "Chargement…" : "Loading…"}
            </div>
          ) : revisions.length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm" data-testid="history-empty">
              {lang === "fr"
                ? "Aucune modification enregistrée. Les versions apparaîtront ici après le premier regroupement, re-fusion, ou correction."
                : "No modifications recorded yet. Versions will appear here after the first regroup, re-merge, or correction."}
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {revisions.map((r) => {
                const when = r.created_at ? new Date(r.created_at) : null;
                const actionLabels = {
                  regroup_segments: lang === "fr" ? "Regroupement Whisper" : "Whisper regroup",
                  re_merge: lang === "fr" ? "Re-fusion Whisper+Fireflies" : "Re-merge Whisper+Fireflies",
                  apply_corrections: lang === "fr" ? "Application de corrections" : "Apply corrections",
                  restore_revision: lang === "fr" ? "Restauration precedente" : "Previous restore",
                  speaker_rename: lang === "fr" ? "Renommage de locuteur" : "Speaker rename",
                  // Tâche 1339 — Sans libellé, l'Historique affichait le nom
                  // technique « edit_merged_segment » au relecteur.
                  edit_merged_segment: lang === "fr" ? "Réécriture d'une phrase" : "Sentence rewrite",
                };
                const label = actionLabels[r.action] || r.action;
                return (
                  <li
                    key={r.id}
                    className="px-5 py-3.5 hover:bg-zinc-50 flex items-start justify-between gap-3"
                    data-testid={`history-item-${r.id.slice(0, 8)}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-800 uppercase tracking-wide">
                          {label}
                        </span>
                        <span className="text-[11px] text-zinc-400">
                          {when ? when.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA") : "—"}
                        </span>
                      </div>
                      <p className="text-[12px] text-zinc-700 leading-snug">{r.summary || "—"}</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">
                        {lang === "fr" ? "Par" : "By"}:{" "}
                        <span className="font-medium text-zinc-700">{r.user_name || r.user_email || "—"}</span>
                      </p>
                      {/* Tâche 1352 — « Voir les changements » : l'avant et
                          l'après côte à côte, sans avoir à restaurer. */}
                      <button
                        type="button"
                        onClick={() => basculer(r.id)}
                        className="mt-1 text-[11px] font-semibold text-[#002FA7] hover:underline inline-flex items-center gap-1"
                        data-testid={`history-toggle-diff-${r.id.slice(0, 8)}`}
                        aria-expanded={!!ouvert[r.id]}
                      >
                        {ouvert[r.id]
                          ? <CaretDown size={11} weight="bold" />
                          : <CaretRight size={11} weight="bold" />}
                        {ouvert[r.id]
                          ? (lang === "fr" ? "Masquer les changements" : "Hide the changes")
                          : (lang === "fr" ? "Voir les changements" : "Show the changes")}
                      </button>
                      {ouvert[r.id] && (
                        <RevisionDiffPanel
                          diff={diffs[r.id]}
                          loading={!!enCours[r.id]}
                          error={erreurs[r.id]}
                          lang={lang}
                        />
                      )}
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => onRestore(r.id)}
                        disabled={restoringId === r.id}
                        className="flex-shrink-0 text-[11px] font-semibold px-3 py-1.5 rounded bg-[#002FA7] text-white hover:bg-[#001F7A] disabled:opacity-60 inline-flex items-center gap-1.5"
                        data-testid={`restore-revision-btn-${r.id.slice(0, 8)}`}
                      >
                        {restoringId === r.id ? (
                          <CircleNotch size={12} className="animate-spin" />
                        ) : (
                          <ArrowClockwise size={12} weight="bold" />
                        )}
                        {lang === "fr" ? "Restaurer" : "Restore"}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
